import { z } from "zod";
import type { AppTranslateFn } from "@/hooks/use-app-translation";

export {
	createOptionalPhoneNumberSchema,
	createPhoneNumberSchema,
} from "@/components/form/validation/create-common-validators";

/**
 * @deprecated Prefer {@link createPhoneNumberSchema} with `t` so messages localize.
 * Kept for Storybook/examples; uses English fallbacks via a no-op that returns the key path last segment — do not use in production forms.
 */
export const phoneNumberSchema = z
	.string()
	.regex(/^\d{10}$/, "Phone must be exactly 10 digits")
	.describe("US phone number (10 digits)");

/**
 * @deprecated Prefer {@link createOptionalPhoneNumberSchema} with `t`.
 */
export const optionalPhoneNumberSchema = z
	.string()
	.regex(/^$|^\d{10}$/, "Phone must be empty or exactly 10 digits")
	.optional()
	.describe("Optional US phone number");

/**
 * Validate phone number with custom message
 */
export const createPhoneValidator = (
	t: AppTranslateFn,
	required = true,
	customMessage?: string,
) => {
	const message = customMessage || t("common.validation.phoneInvalid");

	if (!required) {
		return z
			.string()
			.regex(/^$|^\d{10}$/, message)
			.optional();
	}

	return z.string().regex(/^\d{10}$/, message);
};

/**
 * TanStack Form validator functions for phone numbers (localized).
 */
export const createPhoneValidators = (t: AppTranslateFn) => ({
	onChange: ({ value }: { value: string }) => {
		if (!value || value.length === 0) return undefined;

		const digits = value.replace(/\D/g, "");
		if (digits.length > 0 && digits.length < 10) {
			return t("common.validation.phoneTenDigits");
		}
		return undefined;
	},

	onBlur: ({ value }: { value: string }) => {
		if (!value || value.length === 0) return undefined;

		const digits = value.replace(/\D/g, "");
		if (digits.length !== 10) {
			return t("common.validation.phoneTenDigits");
		}
		return undefined;
	},

	onBlurRequired: ({ value }: { value: string }) => {
		if (!value || value.length === 0) {
			return t("common.validation.phoneRequired");
		}

		const digits = value.replace(/\D/g, "");
		if (digits.length !== 10) {
			return t("common.validation.phoneTenDigits");
		}
		return undefined;
	},

	onChangeAsync: async ({
		value,
		signal,
	}: {
		value: string;
		signal: AbortSignal;
	}) => {
		if (!value || value.length === 0) return undefined;

		const digits = value.replace(/\D/g, "");
		if (digits.length !== 10) return undefined;

		try {
			await new Promise((resolve) => setTimeout(resolve, 500));

			if (signal.aborted) return undefined;

			const blacklistedPrefixes = ["555666", "555777"];
			const prefix = digits.slice(0, 6);

			if (blacklistedPrefixes.includes(prefix)) {
				return t("common.validation.phoneCannotBeUsed");
			}

			return undefined;
		} catch (error) {
			if (error instanceof Error && error.name === "AbortError") {
				return undefined;
			}
			return t("common.validation.phoneValidateFailed");
		}
	},
});

/**
 * @deprecated Prefer {@link createPhoneValidators}. English-only; for Storybook.
 */
export const phoneValidators = {
	onChange: ({ value }: { value: string }) => {
		if (!value || value.length === 0) return undefined;
		const digits = value.replace(/\D/g, "");
		if (digits.length > 0 && digits.length < 10) {
			return "Phone must be 10 digits";
		}
		return undefined;
	},
	onBlur: ({ value }: { value: string }) => {
		if (!value || value.length === 0) return undefined;
		const digits = value.replace(/\D/g, "");
		if (digits.length !== 10) {
			return "Phone must be exactly 10 digits";
		}
		return undefined;
	},
	onBlurRequired: ({ value }: { value: string }) => {
		if (!value || value.length === 0) {
			return "Phone number is required";
		}
		const digits = value.replace(/\D/g, "");
		if (digits.length !== 10) {
			return "Phone must be exactly 10 digits";
		}
		return undefined;
	},
	onChangeAsync: async ({
		value,
		signal,
	}: {
		value: string;
		signal: AbortSignal;
	}) => {
		if (!value || value.length === 0) return undefined;
		const digits = value.replace(/\D/g, "");
		if (digits.length !== 10) return undefined;
		try {
			await new Promise((resolve) => setTimeout(resolve, 500));
			if (signal.aborted) return undefined;
			const blacklistedPrefixes = ["555666", "555777"];
			const prefix = digits.slice(0, 6);
			if (blacklistedPrefixes.includes(prefix)) {
				return "This phone number cannot be used";
			}
			return undefined;
		} catch (error) {
			if (error instanceof Error && error.name === "AbortError") {
				return undefined;
			}
			return "Failed to validate phone number";
		}
	},
};

export const formatPhoneForDisplay = (rawPhone: string): string => {
	const digits = rawPhone.replace(/\D/g, "");
	if (digits.length !== 10) return rawPhone;
	return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const parsePhoneToRaw = (formattedPhone: string): string => {
	return formattedPhone.replace(/\D/g, "");
};

export const isValidUSPhone = (phone: string): boolean => {
	const digits = phone.replace(/\D/g, "");
	return digits.length === 10;
};
